import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { stringify as qsStringify } from 'qs';
import { IStringifyOptions } from 'qs';
import { useSearchParams } from 'react-router-dom';
import { MRT_SortingState } from 'material-react-table';

//MUI
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Box from '@mui/system/Box';
import Visibility from '@mui/icons-material/Visibility';

//common
import { STATUS_OPTIONS } from 'common/constants';
import MyLink from 'common/components/MyLink';
import IconButtonTooltip from 'common/components/IconButtonTooltip';
import NewMuiTable from 'common/components/NewMuiTable';
import { removeEmptyKeys } from 'common/utils';

//screen
import FilterBox, { DepartmentFilterModel, SearchParams } from 'modules/department/list/FilterBox';
import useGetOrganizationsList, {
  convertOrgName,
  type OrganizationInfo,
} from 'modules/department/services/useGetOrganizationsList';

const defaultSearchParams: SearchParams = {
  pageSize: 20,
  pageIndex: 1,
  sorting: [],
  filters: {},
};

const DepartmentPage = () => {
  const [params, setParams] = useState<SearchParams>(defaultSearchParams);
  const [, setSearchParams] = useSearchParams();

  const { data: departments, isLoading } = useGetOrganizationsList();

  const handleChangeSorting = (sorting: MRT_SortingState) => {
    setParams((old) => ({ ...old, sorting }));
  };

  const handleChangePage = (pageIndex: number) => {
    setParams((old) => ({ ...old, pageIndex }));
  };

  const handleChangeRowPerPage = (pageSize: number) => {
    setParams((old) => ({
      ...old,
      pageIndex: 1,
      pageSize,
    }));
  };

  const handleClearFilterParams = () => {
    setParams(defaultSearchParams);
  };

  const handlSetFilterParams = (filters: DepartmentFilterModel) => {
    setParams((old) => ({
      ...old,
      pageIndex: 1,
      pageSize: 20,
      filters: removeEmptyKeys({ ...old.filters, ...filters }),
    }));
  };

  const getColumnFilter = () => {
    const keys = Object.keys(params.filters).filter((key) => key !== 'name');
    return keys.map((key) => ({
      id: key === 'inactive' ? 'status' : key,
      value: params.filters[key],
    }));
  };

  useEffect(() => {
    const { filters, sorting, ...rest } = params;

    const options: IStringifyOptions = {
      arrayFormat: 'brackets',
      skipNulls: true,
    };

    const stringifiedQueries = qsStringify({ ...rest, ...filters }, options);
    setSearchParams(stringifiedQueries, { replace: true });
  }, [params, departments, setSearchParams]);

  return (
    <Paper className="paper-list">
      <NewMuiTable<OrganizationInfo>
        customFilterUi={
          <Box className="paper-filter">
            <FilterBox
              filters={params.filters}
              onSetFilters={handlSetFilterParams}
              onClearFilters={handleClearFilterParams}
            />
          </Box>
        }
        data={departments || []}
        state={{
          isLoading,
          globalFilter: params.filters.name ? params.filters.name.trim() : '',
          columnFilters: getColumnFilter(),
          pagination: {
            pageIndex: params.pageIndex - 1,
            pageSize: params.pageSize,
          },
        }}
        onSortingChange={handleChangeSorting}
        paginationProps={{
          page: params.pageIndex,
          rowsPerPage: params.pageSize,
          onPageChange: handleChangePage,
          onRowsPerPageChange: handleChangeRowPerPage,
        }}
        columns={[
          {
            accessorKey: 'rowNumber',
            header: 'STT',
            Header: () => 'STT',
            size: 60,
            enableSorting: false,
            Cell: ({ row }) => {
              // this page is frontend pagination, so just return rowIndex
              const rowIndex = row.index + 1; // row.index is 0-based
              return rowIndex;
            },
          },
          {
            accessorKey: 'operationCode',
            header: 'operationCode',
            enableSorting: true,
            enableGlobalFilter: true,
            Header: () => <FormattedMessage id="department.operationCode" />,
          },
          {
            accessorKey: 'name',
            header: 'name',
            enableSorting: true,
            enableGlobalFilter: true,
            Header: () => <FormattedMessage id="department.unitName" />,
          },
          {
            accessorKey: 'orgType',
            header: 'orgType',
            enableSorting: true,
            enableColumnFilter: true,
            enableGlobalFilter: true,
            Header: () => <FormattedMessage id="department.organizationType" />,
            Cell: ({ renderedCellValue: name }) => convertOrgName(`${name}`),
          },
          {
            accessorKey: 'status',
            header: 'status',
            enableColumnFilter: true,
            enableGlobalFilter: true,
            Header: () => <FormattedMessage id="statusAdmin" />,
            Cell: ({ renderedCellValue }) => {
              const status = STATUS_OPTIONS.find((v) => v.id === renderedCellValue);

              return (
                status && (
                  <Chip
                    label={<FormattedMessage id={status.name} />}
                    variant="outlined"
                    color={status.color as any}
                    size="small"
                  />
                )
              );
            },
          },
        ]}
        enableRowActions
        renderRowActions={({ row: { original } }) => [
          <MyLink to={`${original.orgType}/${original.operationCode}/view-only`}>
            <IconButtonTooltip tooltipProps={{ title: <FormattedMessage id="quickView" /> }}>
              <Visibility />
            </IconButtonTooltip>
          </MyLink>,
        ]}
      />
    </Paper>
  );
};
export default DepartmentPage;
